import {
  elementOf,
  elementOfPhase,
  pieceById,
  type Board,
  type Element,
  type GameState,
  type Move,
} from '@archon3d/core';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import type { Scene } from '@babylonjs/core/scene';
import { BOARD_CENTER, ELEMENT_COLORS, SPELL_COLORS, TILE_SIZE } from './constants';
import { cellToWorld, challengeStances } from './layout';
import { softDot } from '../softDot';

/**
 * Short, cheap, readable effects on the board for every spell cast. Each effect is one CPU
 * particle system – one draw call for a second or two, then it stops and is disposed once its
 * last particle has died – drawn additively with one shared sprite that is generated here (no
 * image files) and no post-processing, so it costs the same in the headset as on the desktop.
 * The systems emit in the local space of an empty node under the turntable, so the effects turn
 * and scale with the board like the pieces and live in the world, not on the screen.
 */
export interface SpellEffects {
  /** Shows what a spell just did; `before` is the state it was cast on, `after` the result. */
  play(move: Move & { type: 'spell' }, before: GameState, after: GameState): void;
  /** A summoned creature leaves the board: a puff of its element where it stood. */
  dismiss(element: Element, at: Vector3): void;
  /** A taken piece falls: a puff of its side's colour (`#rrggbb`) where it lands. */
  capture(at: Vector3, color: string): void;
  /** Call every frame. */
  update(dtSeconds: number): void;
  dispose(): void;
}

/** One effect: how long it emits, how many particles, and where they start and head. */
interface EffectSpec {
  name: string;
  seconds: number;
  capacity: number;
  /** Particles per second at a point of the effect's progress (0 … 1). */
  rate: (progress: number) => number;
  /** Seconds a particle lives, min and max. */
  life: [number, number];
  /** Particle size in metres, min and max. */
  size: [number, number];
  /** Metres per second along its direction, min and max. */
  speed: [number, number];
  colors: [Color4, Color4];
  /** Metres per second squared, negative pulls down. */
  gravity?: number;
  /** Where a particle starts, in the board's local space, for the effect's progress. */
  position: (progress: number, out: Vector3) => void;
  /** Which way it heads (unit length); straight up when left out. */
  direction?: (progress: number, out: Vector3) => void;
}

interface LiveEffect {
  system: ParticleSystem;
  spec: EffectSpec;
  t: number;
  /** Its first frame has gone by: the system has been animated, so it has particles to show. */
  begun: boolean;
  stopped: boolean;
}

/** Longest a stopped system may linger before it is disposed regardless (e.g. hidden by the arena). */
const LINGER_SECONDS = 4;
const UP = new Vector3(0, 1, 0);

const rand = (min: number, max: number) => min + Math.random() * (max - min);

function rgba(hex: string, alpha = 1): Color4 {
  const c = Color3.FromHexString(hex);
  return new Color4(c.r, c.g, c.b, alpha);
}

export function createSpellEffects(
  scene: Scene,
  board: Board,
  parent: TransformNode | null,
): SpellEffects {
  const sprite = softDot(scene, 'spellSprite');
  // An empty mesh (nothing to draw) at the board's origin: the systems emit in its local space,
  // which is the turntable's, so `cellToWorld` positions can be used as they are.
  const emitter = new Mesh('spellEffects', scene);
  emitter.parent = parent;
  emitter.isPickable = false;
  const live: LiveEffect[] = [];
  const centre = cellToWorld(board, (board.width - 1) / 2, (board.height - 1) / 2);
  let disposed = false;

  const run = (spec: EffectSpec) => {
    if (disposed) return;
    const system = new ParticleSystem(`spell-${spec.name}`, spec.capacity, scene);
    const effect: LiveEffect = { system, spec, t: 0, begun: false, stopped: false };
    system.particleTexture = sprite;
    system.emitter = emitter;
    system.isLocal = true;
    // One update unit per frame at 60 Hz, so lifetimes are seconds and speeds metres per second.
    system.updateSpeed = 1 / 60;
    system.blendMode = ParticleSystem.BLENDMODE_ADD;
    system.minLifeTime = spec.life[0];
    system.maxLifeTime = spec.life[1];
    system.minSize = spec.size[0];
    system.maxSize = spec.size[1];
    system.minEmitPower = spec.speed[0];
    system.maxEmitPower = spec.speed[1];
    system.color1 = spec.colors[0];
    system.color2 = spec.colors[1];
    system.colorDead = new Color4(0, 0, 0, 0);
    system.gravity = new Vector3(0, spec.gravity ?? 0, 0);
    system.emitRate = spec.rate(0);
    system.startPositionFunction = (_matrix, out) =>
      spec.position(Math.min(1, effect.t / spec.seconds), out);
    system.startDirectionFunction = (_matrix, out) => {
      if (spec.direction) spec.direction(Math.min(1, effect.t / spec.seconds), out);
      else out.copyFrom(UP);
    };
    system.start();
    live.push(effect);
  };

  /** A point on a ring of the given radius around a square, `height` tiles above it. */
  const onRing = (at: Vector3, radius: number, height: number, out: Vector3) => {
    const angle = Math.random() * Math.PI * 2;
    out.set(
      at.x + Math.cos(angle) * radius * TILE_SIZE,
      at.y + height * TILE_SIZE,
      at.z + Math.sin(angle) * radius * TILE_SIZE,
    );
  };
  /** A point on a disc of the given radius around a square, just above it. */
  const onDisc = (at: Vector3, radius: number, out: Vector3) =>
    onRing(at, Math.sqrt(Math.random()) * radius, 0.03, out);

  const shimmer = (name: string, cells: Vector3[], hex: string) => {
    let n = 0;
    run({
      name,
      seconds: 0.8,
      capacity: 160,
      rate: () => 150,
      life: [0.5, 0.9],
      size: [0.006, 0.013],
      speed: [0.05, 0.13],
      colors: [rgba(hex), rgba('#ffffff', 0.9)],
      position: (_p, out) => onDisc(cells[n++ % cells.length]!, 0.32, out),
    });
  };

  const burst = (name: string, at: Vector3, hex: string, strength: number) => {
    run({
      name,
      seconds: 0.3,
      capacity: Math.ceil(130 * strength),
      rate: (p) => (p < 0.35 ? 900 * strength : 0),
      life: [0.5, 1.0],
      size: [0.012, 0.024],
      speed: [0.25 * strength, 0.65 * strength],
      colors: [rgba(hex), rgba('#ffffff')],
      gravity: -0.6,
      position: (_p, out) => onDisc(at, 0.12, out),
      direction: (_p, out) => {
        // Outward and mostly upward, like sparks off an anvil.
        const angle = Math.random() * Math.PI * 2;
        const lift = 0.4 + Math.random() * 0.6;
        out.set(Math.cos(angle) * (1 - lift), lift, Math.sin(angle) * (1 - lift)).normalize();
      },
    });
  };

  const summonAt = (
    move: Move & { type: 'spell'; spell: 'summon-elemental' },
    after: GameState,
  ) => {
    // The elemental waits on its victim's square while the fight is pending, off to the side it
    // was conjured on (see layout.ts): the summoning shows where it actually stands.
    const battle = after.pendingBattle;
    const at = battle
      ? challengeStances(board, { x: move.x, y: move.y }, { x: battle.x, y: battle.y }).attacker
      : cellToWorld(board, move.x, move.y);
    const attacker = battle ? pieceById(after, battle.attackerId) : undefined;
    const element =
      (attacker && elementOf(attacker.character)) ?? elementOfPhase(after.luminosity.phase);
    burst('summon', at, ELEMENT_COLORS[element], 1);
  };

  const play: SpellEffects['play'] = (move, before, after) => {
    switch (move.spell) {
      case 'teleport': {
        const piece = pieceById(before, move.pieceId);
        const cells = [cellToWorld(board, move.x, move.y)];
        if (piece) cells.unshift(cellToWorld(board, piece.x, piece.y));
        shimmer('teleport', cells, SPELL_COLORS.teleport);
        return;
      }
      case 'heal': {
        const piece = pieceById(after, move.pieceId);
        if (!piece) return;
        const at = cellToWorld(board, piece.x, piece.y);
        run({
          name: 'heal',
          seconds: 1.2,
          capacity: 140,
          rate: (p) => 130 * (1 - p * 0.6),
          life: [0.6, 1.1],
          size: [0.01, 0.02],
          speed: [0.04, 0.09],
          colors: [rgba(SPELL_COLORS.heal), rgba('#d8ffd8', 0.8)],
          position: (_p, out) => onDisc(at, 0.3, out),
        });
        return;
      }
      case 'shift-time': {
        // A ripple: the cycling squares light up from the middle of the board outwards.
        const cells = board.cells
          .filter((c) => c.luminosity === 'cycling')
          .map((c) => cellToWorld(board, c.x, c.y))
          .sort((a, b) => Vector3.Distance(a, centre) - Vector3.Distance(b, centre));
        if (cells.length === 0) cells.push(centre.clone());
        run({
          name: 'shift-time',
          seconds: 1.3,
          capacity: 260,
          rate: () => Math.min(220, (7 * cells.length) / 1.3),
          life: [0.5, 0.9],
          size: [0.014, 0.026],
          speed: [0.02, 0.05],
          colors: [rgba(SPELL_COLORS['shift-time']), rgba('#ffffff', 0.9)],
          position: (p, out) => {
            const front = p * (cells.length - 1);
            const i = Math.round(Math.min(cells.length - 1, Math.max(0, front + rand(-1.5, 1.5))));
            onDisc(cells[i]!, 0.36, out);
          },
        });
        return;
      }
      case 'exchange': {
        const a = pieceById(before, move.pieceId);
        const b = pieceById(before, move.otherId);
        if (!a || !b) return;
        const from = cellToWorld(board, a.x, a.y);
        const to = cellToWorld(board, b.x, b.y);
        // Sideways of the line between the two, so the arcs pass beside each other.
        const side = new Vector3(to.z - from.z, 0, from.x - to.x)
          .normalize()
          .scale(0.18 * TILE_SIZE);
        const arcs = [
          [from, to, side],
          [to, from, side.scale(-1)],
        ] as const;
        let n = 0;
        run({
          name: 'exchange',
          seconds: 0.9,
          capacity: 220,
          rate: () => 240,
          life: [0.45, 0.8],
          size: [0.008, 0.016],
          speed: [0, 0.02],
          colors: [rgba(SPELL_COLORS.exchange), rgba('#ffffff')],
          position: (p, out) => {
            const [start, end, offset] = arcs[n++ % 2]!;
            Vector3.LerpToRef(start, end, p, out);
            out.addInPlace(offset);
            out.y += Math.sin(p * Math.PI) * 0.6 * TILE_SIZE + 0.01;
          },
        });
        return;
      }
      case 'revive': {
        const at = cellToWorld(board, move.x, move.y);
        run({
          name: 'revive',
          seconds: 1.3,
          capacity: 240,
          rate: (p) => 230 * (1 - p),
          life: [0.5, 0.9],
          size: [0.01, 0.02],
          speed: [0.15, 0.32],
          colors: [rgba(SPELL_COLORS.revive), rgba('#ffd27a')],
          position: (_p, out) => onDisc(at, 0.25, out),
        });
        return;
      }
      case 'imprison': {
        const piece = pieceById(after, move.pieceId);
        if (!piece) return;
        const at = cellToWorld(board, piece.x, piece.y);
        run({
          name: 'imprison',
          seconds: 1.2,
          capacity: 240,
          rate: () => 230,
          life: [0.5, 0.9],
          size: [0.007, 0.012],
          speed: [0, 0.01],
          colors: [rgba(SPELL_COLORS.imprison), rgba('#d2b4ff')],
          // Rings that settle: high and wide at first, tight around the feet at the end.
          position: (p, out) => onRing(at, 0.36 - 0.14 * p, 0.95 - 0.9 * p, out),
        });
        return;
      }
      case 'summon-elemental':
        summonAt(move, after);
        return;
    }
  };

  // Warm the particle shader up with a tiny invisible effect, so the first spell does not stall.
  run({
    name: 'warm-up',
    seconds: 0.1,
    capacity: 2,
    rate: () => 20,
    life: [0.05, 0.1],
    size: [0.0001, 0.0001],
    speed: [0, 0],
    colors: [new Color4(0, 0, 0, 0), new Color4(0, 0, 0, 0)],
    position: (_p, out) => out.copyFrom(BOARD_CENTER),
  });

  return {
    play,
    dismiss(element, at) {
      burst('dismiss', at, ELEMENT_COLORS[element], 0.5);
    },
    capture(at, color) {
      burst('capture', at, color, 0.4);
    },
    update(dt) {
      for (let i = live.length - 1; i >= 0; i--) {
        const effect = live[i]!;
        // Its first frame only starts it. A slow frame (a hitch, a busy machine) used to stop and
        // dispose it before Babylon had animated it once, so the puff was never seen.
        if (!effect.begun) {
          effect.begun = true;
          continue;
        }
        effect.t += dt;
        const progress = effect.t / effect.spec.seconds;
        if (progress < 1) {
          effect.system.emitRate = effect.spec.rate(progress);
          continue;
        }
        if (!effect.stopped) {
          effect.system.stop();
          effect.stopped = true;
          // Babylon says whether particles are left only once it has animated the system since.
          continue;
        }
        if (!effect.system.isAlive() || effect.t > effect.spec.seconds + LINGER_SECONDS) {
          // The sprite is shared: keep it.
          effect.system.dispose(false);
          live.splice(i, 1);
        }
      }
    },
    dispose() {
      disposed = true;
      for (const effect of live) effect.system.dispose(false);
      live.length = 0;
      sprite.dispose();
      emitter.dispose();
    },
  };
}
